import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import EntityService from '../../services/EntityService';
import { API } from '../../shared/api/Api';
import { handleFormValidationErrors } from '../../utils/utils';

/**
 * Fetch all users
 */
export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async ({ token, params }, { rejectWithValue }) => {
    try {
      const response = await EntityService.getAllEnties(
        token,
        API.Users.app_users_allUsers,
        params
      );
      if (response.data.status === 200 && response.data.errorCode === 0) {
        return {
          items: response.data.data.items,
          currentPage: response.data.data.current_page,
          totalPages: response.data.data.last_page,
          total: response.data.data.total,
        };
      }
      return rejectWithValue(response.data.message || 'Failed to fetch users');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch users');
    }
  }
);

/**
 * Fetch a single user by ID
 */
export const fetchUserById = createAsyncThunk(
  'users/fetchUserById',
  async ({ token, userId }, { rejectWithValue }) => {
    try {
      const response = await EntityService.getEntity(
        token,
        API.Users.userDetails,
        userId
      );
      if (response.data.status === 200 && response.data.errorCode === 0) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to fetch user');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user');
    }
  }
);

/**
 * Create a new user
 */
export const createUser = createAsyncThunk(
  'users/createUser',
  async ({ token, formData }, { rejectWithValue }) => {
    try {
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'twoFactorAuth') {
          data.append('twoFactorAuth', value ? 1 : 0);
        } else {
          data.append(key, value);
        }
      });

      const response = await EntityService.addEntity(
        token,
        API.Users.app_users_add,
        data
      );
      if (response.data.status === 200 || response.data.status === 201) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to create user');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create user';
      const validationErrors = error.response?.data?.data || {};
      return rejectWithValue({ message: errorMessage, errors: validationErrors });
    }
  }
);

/**
 * Update an existing user
 * Only sends filled/changed fields, excludes empty values
 */
export const updateUser = createAsyncThunk(
  'users/updateUser',
  async ({ token, userId, formData, initialData }, { rejectWithValue }) => {
    try {
      const data = new FormData();
      
      // Helper function to check if a value is considered "filled" (has actual content)
      const isFilled = (value) => {
        if (value === null || value === undefined) return false;
        if (typeof value === 'string' && value.trim() === '') return false;
        if (typeof value === 'boolean') return true; // Boolean values are always considered filled
        if (value instanceof File) return true; // Files are always considered filled
        if (Array.isArray(value) && value.length === 0) return false; // Empty arrays are not filled
        return true;
      };
      
      // Only append fields that are filled (have values) and changed from initial
      Object.keys(formData).forEach((key) => {
        const currentValue = formData[key];
        const initialValue = initialData?.[key];

        // Skip if value is not filled (empty string, null, undefined, empty array)
        if (!isFilled(currentValue)) {
          return;
        }

        // Handle file inputs - always send if it's a new file
        if (currentValue instanceof File) {
          data.append(key, currentValue);
          return;
        }

        // Handle password fields - only send if they have values (user wants to change password)
        // Don't compare with initial value, just send if filled
        if (key === 'password' || key === 'password_confirmation') {
          data.append(key, currentValue);
          return;
        }

        // Handle twoFactorAuth - send if value has changed from initial
        if (key === 'twoFactorAuth') {
          // Only send if value has changed
          if (currentValue !== initialValue) {
            data.append('twoFactorAuth', currentValue ? 1 : 0);
          }
          return;
        }

        // For all other fields, only send if:
        // 1. Value is filled (has content)
        // 2. Value has changed from initial value
        if (currentValue !== initialValue) {
          data.append(key, currentValue);
        }
      });

      const response = await EntityService.updateEntity(
        token,
        API.Users.app_users_update,
        userId,
        data
      );
      if (response.data.status === 200) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to update user');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update user';
      const validationErrors = error.response?.data?.data || {};
      return rejectWithValue({ message: errorMessage, errors: validationErrors });
    }
  }
);

/**
 * Delete a user
 */
export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async ({ token, userId }, { rejectWithValue }) => {
    try {
      const response = await EntityService.deleteEntity(
        token,
        API.Users.app_users_delete,
        userId
      );
      if (response.data.status === 200) {
        return userId;
      }
      return rejectWithValue(response.data.message || 'Failed to delete user');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete user');
    }
  }
);

const initialState = {
  // List state
  entities: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    total: 0,
  },
  
  // Selected entity state
  selectedEntity: null,
  initialFormData: null,
  
  // Status states
  listStatus: 'idle', // idle | loading | succeeded | failed
  detailStatus: 'idle',
  createStatus: 'idle',
  updateStatus: 'idle',
  deleteStatus: 'idle',
  
  // Error states
  listError: null,
  detailError: null,
  createError: null,
  updateError: null,
  deleteError: null,
  
  // Form validation errors
  formErrors: {},
};

const userSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    // Reset selected entity
    clearSelectedEntity: (state) => {
      state.selectedEntity = null;
      state.initialFormData = null;
      state.detailStatus = 'idle';
      state.detailError = null;
      state.formErrors = {};
    },
    
    // Reset form errors
    clearFormErrors: (state) => {
      state.formErrors = {};
      state.createError = null;
      state.updateError = null;
    },
    
    // Reset create/update status
    resetSubmissionStatus: (state) => {
      state.createStatus = 'idle';
      state.updateStatus = 'idle';
      state.createError = null;
      state.updateError = null;
      state.formErrors = {};
    },
    
    // Update form data locally (for controlled inputs)
    updateFormData: (state, action) => {
      if (state.selectedEntity) {
        state.selectedEntity = {
          ...state.selectedEntity,
          ...action.payload,
        };
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch users list
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.listStatus = 'loading';
        state.listError = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.listStatus = 'succeeded';
        state.entities = action.payload.items;
        state.pagination = {
          currentPage: action.payload.currentPage,
          totalPages: action.payload.totalPages,
          total: action.payload.total,
        };
        state.listError = null;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.listStatus = 'failed';
        state.listError = action.payload;
      });

    // Fetch user by ID
    builder
      .addCase(fetchUserById.pending, (state) => {
        state.detailStatus = 'loading';
        state.detailError = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.detailStatus = 'succeeded';
        state.selectedEntity = action.payload;
        state.initialFormData = { ...action.payload };
        state.detailError = null;
        state.formErrors = {};
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.detailStatus = 'failed';
        state.detailError = action.payload;
      });

    // Create user
    builder
      .addCase(createUser.pending, (state) => {
        state.createStatus = 'loading';
        state.createError = null;
        state.formErrors = {};
      })
      .addCase(createUser.fulfilled, (state) => {
        state.createStatus = 'succeeded';
        state.createError = null;
        state.formErrors = {};
      })
      .addCase(createUser.rejected, (state, action) => {
        state.createStatus = 'failed';
        const errorData = action.payload;
        state.createError = errorData.message || 'Failed to create user';
        state.formErrors = errorData.errors || {};
      });

    // Update user
    builder
      .addCase(updateUser.pending, (state) => {
        state.updateStatus = 'loading';
        state.updateError = null;
        state.formErrors = {};
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';
        state.selectedEntity = action.payload;
        state.initialFormData = { ...action.payload };
        state.updateError = null;
        state.formErrors = {};
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.updateStatus = 'failed';
        const errorData = action.payload;
        state.updateError = errorData.message || 'Failed to update user';
        state.formErrors = errorData.errors || {};
      });

    // Delete user
    builder
      .addCase(deleteUser.pending, (state) => {
        state.deleteStatus = 'loading';
        state.deleteError = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.entities = state.entities.filter(user => user.id !== action.payload);
        state.deleteError = null;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.deleteError = action.payload;
      });
  },
});

export const {
  clearSelectedEntity,
  clearFormErrors,
  resetSubmissionStatus,
  updateFormData,
} = userSlice.actions;

// Selectors
export const selectUsers = (state) => state.users.entities;
export const selectUsersPagination = (state) => state.users.pagination;
export const selectSelectedUser = (state) => state.users.selectedEntity;
export const selectInitialFormData = (state) => state.users.initialFormData;
export const selectUsersListStatus = (state) => state.users.listStatus;
export const selectUserDetailStatus = (state) => state.users.detailStatus;
export const selectUserCreateStatus = (state) => state.users.createStatus;
export const selectUserUpdateStatus = (state) => state.users.updateStatus;
export const selectUserDeleteStatus = (state) => state.users.deleteStatus;
export const selectUsersListError = (state) => state.users.listError;
export const selectUserDetailError = (state) => state.users.detailError;
export const selectUserCreateError = (state) => state.users.createError;
export const selectUserUpdateError = (state) => state.users.updateError;
export const selectUserDeleteError = (state) => state.users.deleteError;
export const selectUserFormErrors = (state) => state.users.formErrors;

export default userSlice.reducer;
